import {
	useContext,
	ReactNode,
	useLayoutEffect,
	createContext,
	useState,
} from 'react';
import { useLocalStorage } from '@mantine/hooks';
import { Admin, AppUser, UserType } from '@/types/users';
import { LoginValues, SubmitStatus } from '@/pages/unauthenticated/Login';
import { useQueryClient } from '@tanstack/react-query';
import { jwtDecode, JwtPayload } from 'jwt-decode';
import { UUID } from '@/types/types';
import UnauthenticatedApi from '@/api/UnauthenticatedApi';
import APP_CONFIG from '@/configs/appConfig';
import httpClient from '@/api/httpClient';
import Cookies from 'js-cookie';
import UserApi from '@/api/UserApi';

interface TokenPayload extends JwtPayload {
	id: UUID;
	sessionId: UUID;
	userType: UserType;
}

interface Context {
	user: AppUser | null;
	changeUser: (newUser?: AppUser | null) => void;
	login: (values: LoginValues) => Promise<number>;
	logout: () => void;
}

const AuthContext = createContext<Context>(null!);

export const useAuth = () => useContext(AuthContext);

export const AuthProvider = ({ children }: { children: ReactNode }) => {
	const queryClient = useQueryClient();

	const [user, setUser] = useLocalStorage<AppUser | null>({
		key: 'user',
		defaultValue: null,
		getInitialValueInEffect: false,
	});

	const [refreshToken, setRefreshToken, clearRefreshToken] = useLocalStorage<
		string | undefined
	>({
		key: APP_CONFIG.SESSION_STORAGE_KEYS.REFRESH_TOKEN,
		defaultValue: Cookies.get(APP_CONFIG.SESSION_STORAGE_KEYS.REFRESH_TOKEN),
		getInitialValueInEffect: false,
	});

	const [refreshTimeout, setRefreshTimeout] = useState<number>();

	useLayoutEffect(() => {
		loginWithResfreshToken(refreshToken);
	}, []);

	const login: Context['login'] = async ({ email, password }) => {
		try {
			const response = await UnauthenticatedApi.login({ email, password });

			const { token, refreshToken } = response.data;

			const decoded = jwtDecode<TokenPayload>(token);

			await setAuth(decoded, token, refreshToken);

			return SubmitStatus.SUCCESS;
		} catch (error) {
			console.error(error);
			return SubmitStatus.ERROR;
		}
	};

	const loginWithResfreshToken = async (refresh?: string) => {
		if (!refresh) {
			logout();
			return;
		}

		try {
			const response = await UnauthenticatedApi.refreshToken(refresh);

			const { token, refreshToken } = response.data;

			const decoded = jwtDecode<TokenPayload>(token);

			setAuth(decoded, token, refreshToken);
		} catch (error: any) {
			console.error(error);
			if (error.message !== 'Request aborted') {
				setUser(null);
				eradicateRefreshToken();
			}
		}
	};

	const refreshTokenWhenExpire = (token: TokenPayload, refresh: string) => {
		const tokenLifeSpan = token.iat && token.exp ? token.exp - token.iat : 0;
		const refreshTimeout = (tokenLifeSpan - 15) * 1000;

		const id = setTimeout(() => {
			loginWithResfreshToken(refresh);
		}, refreshTimeout);

		setRefreshTimeout(id);
	};

	const logout: Context['logout'] = () => {
		UnauthenticatedApi.logout();
		queryClient.cancelQueries();
		queryClient.clear();
		eradicateRefreshToken();
		setUser(null);
		httpClient.defaults.headers.common['Authorization'] = '';
		clearTimeout(refreshTimeout);
	};

	const eradicateRefreshToken = () => {
		clearRefreshToken();
		Cookies.remove(APP_CONFIG.SESSION_STORAGE_KEYS.REFRESH_TOKEN);
	};

	const saveRefreshToken = (refreshToken: string) => {
		setRefreshToken(refreshToken);
		Cookies.set(APP_CONFIG.SESSION_STORAGE_KEYS.REFRESH_TOKEN, refreshToken, {
			secure: true,
			expires: 360,
		});
	};

	const setAuth = async (
		decoded: TokenPayload,
		token: string,
		refreshToken: string
	) => {
		setAuthHeader(token);

		const currentUser = await UserApi.getCurrentUser()

		saveRefreshToken(refreshToken);

		refreshTokenWhenExpire(decoded, refreshToken);

		setUser(currentUser);
	};

	const changeUser: Context['changeUser'] = (newUser = null) =>
		setUser(newUser);

	return (
		<AuthContext
			value={{
				user,
				changeUser,
				login,
				logout,
			}}
		>
			{children}
		</AuthContext>
	);
};

function setAuthHeader(token: string) {
	httpClient.defaults.headers.common['Authorization'] = `Bearer ${token}`;
}
